import { useEffect, useMemo, useState } from 'react';
import {
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, List, Loader, Plus } from 'lucide-react';
import { cn } from '../utils/cn';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useCalendarLinks } from '../hooks/useCalendarLinks';
import { getCalendarLinkSources } from '../utils/calendarLinkAccess';
import { eventsOverlapping } from '../utils/calendarLayout';
import type { CalendarEvent, CalendarTag } from '../types/calendar';
import { TimelineView } from './TimelineView';
import { AgendaView } from './calendar/AgendaView';
import { MonthGrid } from './calendar/MonthGrid';
import type { TagColorLookup } from './calendar/eventColors';

interface CalendarViewProps {
  events: CalendarEvent[];
  tags: CalendarTag[];
  onEventClick: (event: CalendarEvent) => void;
  /** Adds a "New event" action; the parent only passes it when the user may create events. */
  onCreateEvent?: () => void;
  /** Month to open on. Defaults to today; the view owns it from then on. */
  visibleMonth?: Date;
  /** Fires when the user pages, so the parent can load that month's events. */
  onVisibleMonthChange?: (date: Date) => void;
  /** A background fetch for another month is in flight. */
  isFetching?: boolean;
}

type ViewMode = 'month' | 'timeline' | 'agenda';

const VIEW_MODES: { id: ViewMode; label: string; icon: typeof CalendarDays; desktopOnly?: boolean }[] = [
  { id: 'month', label: 'Month', icon: CalendarDays, desktopOnly: true },
  { id: 'timeline', label: 'Timeline', icon: Clock, desktopOnly: true },
  { id: 'agenda', label: 'Agenda', icon: List },
];

export const CalendarView = ({
  events,
  tags,
  onEventClick,
  onCreateEvent,
  visibleMonth,
  onVisibleMonthChange,
  isFetching,
}: CalendarViewProps) => {
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const isMobile = useIsMobile();
  const [currentDate, setCurrentDate] = useState(() => visibleMonth ?? new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(isMobile ? 'agenda' : 'month');

  // Tell the parent which month to keep loaded, so it can fetch just that window.
  useEffect(() => {
    onVisibleMonthChange?.(currentDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format(currentDate, 'yyyy-MM')]);

  const permissions = useMemo(() => identity?.raw.custom.permissions || [], [identity]);

  // The grid depends only on the month, the timeline only on the week, so each
  // is keyed on that rather than recomputed for every unrelated render.
  const monthLabel = format(currentDate, 'yyyy-MM');
  const { monthStart, monthEnd, gridStart, gridEnd } = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return { monthStart: start, monthEnd: end, gridStart: startOfWeek(start), gridEnd: endOfWeek(end) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthLabel]);

  const weekLabel = format(startOfWeek(currentDate), 'yyyy-MM-dd');
  const { weekStart, weekEnd } = useMemo(
    () => ({ weekStart: startOfWeek(currentDate), weekEnd: endOfWeek(currentDate) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekLabel],
  );

  const tagColors: TagColorLookup = useMemo(() => new Map(tags.map(tag => [tag.id, tag.color])), [tags]);

  // Only what is on screen is looked up in the team modules.
  const visibleEvents = useMemo(() => {
    if (viewMode === 'timeline') return eventsOverlapping(events, weekStart, weekEnd);
    if (viewMode === 'agenda') return eventsOverlapping(events, monthStart, monthEnd);
    return eventsOverlapping(events, gridStart, gridEnd);
  }, [events, viewMode, weekStart, weekEnd, monthStart, monthEnd, gridStart, gridEnd]);

  const linkSources = useMemo(() => getCalendarLinkSources(identity, isDev), [identity, isDev]);
  const links = useCalendarLinks(visibleEvents, linkSources);

  const step = (direction: 1 | -1) => {
    setCurrentDate(current =>
      viewMode === 'timeline'
        ? direction > 0
          ? addWeeks(current, 1)
          : subWeeks(current, 1)
        : direction > 0
          ? addMonths(current, 1)
          : subMonths(current, 1),
    );
  };

  const modes = VIEW_MODES.filter(mode => !mode.desktopOnly || !isMobile);

  const title =
    viewMode === 'timeline'
      ? `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`
      : format(currentDate, 'MMMM yyyy');

  return (
    <div className="space-y-4">
      <div className="glass-card overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 p-3 md:p-4 md:flex-row md:items-center md:justify-between border-b border-glass-200/10">
          <div className="flex items-center gap-1.5 min-w-0">
            <button
              type="button"
              onClick={() => step(-1)}
              className="btn-icon"
              title={viewMode === 'timeline' ? 'Previous week' : 'Previous month'}
              aria-label={viewMode === 'timeline' ? 'Previous week' : 'Previous month'}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setCurrentDate(new Date())} className="glass-button-secondary btn-xs">
              Today
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              className="btn-icon"
              title={viewMode === 'timeline' ? 'Next week' : 'Next month'}
              aria-label={viewMode === 'timeline' ? 'Next week' : 'Next month'}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <h2 className="ml-2 text-base font-semibold tracking-tight text-white tabular-nums truncate">{title}</h2>
            <span className="hidden sm:inline text-xs text-glass-500 ml-1 truncate">
              {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </span>
            {isFetching && <Loader className="w-4 h-4 animate-spin text-accent-400 shrink-0 ml-1" aria-label="Loading events" />}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {modes.length > 1 && (
              <div className="segmented">
                {modes.map(mode => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setViewMode(mode.id)}
                    className="segmented-item segmented-item-sm"
                    data-active={viewMode === mode.id ? 'true' : 'false'}
                  >
                    <mode.icon className="w-3.5 h-3.5" />
                    <span>{mode.label}</span>
                  </button>
                ))}
              </div>
            )}
            {onCreateEvent && (
              <button type="button" onClick={onCreateEvent} className="glass-button-sm touch-manipulation">
                <Plus className="w-3.5 h-3.5" />
                New event
              </button>
            )}
          </div>
        </div>

        {viewMode === 'month' && (
          <MonthGrid
            events={events}
            currentDate={currentDate}
            gridStart={gridStart}
            gridEnd={gridEnd}
            tagColors={tagColors}
            links={links}
            onEventClick={onEventClick}
            maxLanes={isMobile ? 2 : 4}
            compact={isMobile}
          />
        )}

        {viewMode === 'timeline' && (
          <TimelineView
            events={events}
            tagColors={tagColors}
            links={links}
            currentDate={currentDate}
            onEventClick={onEventClick}
          />
        )}

        {viewMode === 'agenda' && (
          <div className="p-3 md:p-4">
            <AgendaView
              events={events}
              tagColors={tagColors}
              links={links}
              currentDate={currentDate}
              onEventClick={onEventClick}
            />
          </div>
        )}
      </div>

      {/* Legend */}
      {(tags.length > 0 || linkSources.length > 0) && (
        <div className="glass-card p-3 md:p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {tags.map(tag => {
                // The creator of a restricted tag may not hold its permission yet.
                const needsPermission =
                  tag.visibility === 'restricted' &&
                  identity?.user_uuid === tag.created_by &&
                  !permissions.includes(`argus.calendar.tag.${tag.name}`);
                return (
                  <span key={tag.id} className="relative group inline-flex items-center gap-1.5 text-xs text-glass-300">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                    {tag.name}
                    {needsPermission && (
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-dark-800 border border-glass-200/20 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-popover">
                        Request permission:{' '}
                        <code className="bg-dark-900 px-1 py-0.5 rounded">argus.calendar.tag.{tag.name}</code>
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          )}
          <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-glass-400', tags.length > 0 && 'md:ml-auto')}>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-5 h-3 rounded-sm bg-glass-300/25 shadow-[inset_2px_0_0_currentColor] shrink-0" aria-hidden />
              All day / multi-day
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-glass-300/70 shrink-0" aria-hidden />
              Timed
            </span>
            {linkSources.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-5 h-3 rounded-sm border border-dashed border-glass-300/60 shrink-0" aria-hidden />
                Linked from a team module
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
